import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { InlineCalendar } from "@/components/ui/InlineCalendar";

/**
 * Deadline (määräaika) picker: quick preset chips over an always-visible
 * inline calendar. The chips cover the common cases (today / tomorrow / +1–2
 * weeks); the calendar handles everything else without a "click to open" step.
 *
 * The value is the same `"YYYY-MM-DD"` string the rest of the form uses.
 */

/** Local-time `"YYYY-MM-DD"`, `dayOffset` days from today. */
function ymdFromToday(dayOffset: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function DeadlineField({
  value,
  onChange,
}: {
  value: string;
  onChange: (yyyyMmDd: string) => void;
}) {
  const chips = useMemo(
    () => [
      { label: "Tänään", date: ymdFromToday(0) },
      { label: "Huomenna", date: ymdFromToday(1) },
      { label: "+1 vko", date: ymdFromToday(7) },
      { label: "+2 vko", date: ymdFromToday(14) },
      { label: "+3 vko", date: ymdFromToday(21) },
    ],
    [],
  );

  return (
    <div className="grid gap-2">
      <div className="mt-1 flex flex-wrap justify-center gap-2">
        {chips.map((chip) => {
          const active = value === chip.date;
          return (
            <button
              key={chip.label}
              type="button"
              onClick={() => onChange(chip.date)}
              className={cn(
                "rounded-full border px-3 py-1 text-sm transition-colors",
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
              )}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
      <InlineCalendar value={value} onChange={onChange} className="mx-auto" />
    </div>
  );
}
